import { useState, useEffect } from "react";
import api from "@/lib/api";
import { DollarSign, ShoppingCart, Package, Users, Clock, Banknote } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

export default function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get("/reports/dashboard");
        setStats(res.data);
      } catch (err) {
        console.error("Failed to fetch stats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" />
      </div>
    );
  }

  const cards = [
    { label: "Today's Revenue", value: `$${(stats?.today_revenue || 0).toFixed(2)}`, icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Today's Orders", value: stats?.today_orders || 0, icon: ShoppingCart, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Today's Labor", value: `$${(stats?.today_labor || 0).toFixed(2)}`, icon: Clock, color: "text-orange-600", bg: "bg-orange-50", sub: stats?.active_clocked_in > 0 ? `${stats.active_clocked_in} on clock` : null },
    { label: "Active Products", value: stats?.total_products || 0, icon: Package, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Employees", value: stats?.total_employees || 0, icon: Users, color: "text-violet-600", bg: "bg-violet-50" },
    { label: "Today's Tips", value: `$${(stats?.today_tips || 0).toFixed(2)}`, icon: Banknote, color: "text-teal-600", bg: "bg-teal-50" },
  ];

  return (
    <div className="p-8" data-testid="dashboard-overview">
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="overview-title">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Welcome back. Here's what's happening today.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {cards.map((card, i) => (
          <div key={i} className={`stat-card animate-fade-in stagger-${i + 1}`} data-testid={`stat-card-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-slate-400 font-medium">{card.label}</span>
              <div className={`p-2 rounded-lg ${card.bg}`}>
                <card.icon className={`h-4 w-4 ${card.color}`} />
              </div>
            </div>
            <p className="font-heading text-2xl font-bold text-slate-900">{card.value}</p>
            {card.sub && <p className="text-xs text-emerald-600 mt-1 font-medium">{card.sub}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="total-stats-card">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">All-Time Summary</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">Total Revenue</span>
              <span className="text-sm font-semibold text-slate-900">${(stats?.total_revenue || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">Total Orders</span>
              <span className="text-sm font-semibold text-slate-900">{stats?.total_orders || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">Avg. Order Value</span>
              <span className="text-sm font-semibold text-slate-900">
                ${stats?.total_orders ? ((stats?.total_revenue || 0) / stats.total_orders).toFixed(2) : "0.00"}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="recent-orders-card">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Recent Orders</h3>
          {stats?.recent_orders?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Order</TableHead>
                  <TableHead className="text-xs text-right">Total</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.recent_orders.slice(0, 5).map((order) => (
                  <TableRow key={order.order_id}>
                    <TableCell className="text-sm font-medium">{order.order_id?.slice(-8)}</TableCell>
                    <TableCell className="text-sm text-right">${(order.total || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={order.status === "completed" ? "default" : "secondary"} className="text-xs">
                        {order.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-slate-400 text-center py-8">No orders yet. Open the POS to start selling!</p>
          )}
        </div>
      </div>
    </div>
  );
}
